import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Table, Alert } from "react-bootstrap";
import {
  GetTrainTickets,
  GetTrainTicketById,
  DuplicateTrainTicket,
  ArchiveTrainTicket,
  PreviewTrainTicketById,
  DownloadTrainTicketPdfById,
} from "../../entities/action/action";
import { BOOKING_STATUS_OPTIONS, PAYMENT_STATUS_OPTIONS, mapTicketToForm } from "./trainTicketConstants";
import classes from "./ticketGenerator.module.css";

const TrainTicketList = ({ onEdit, onCreateNew }) => {
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");
  const [journeyDate, setJourneyDate] = useState("");
  const [bookingStatus, setBookingStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [previewHtml, setPreviewHtml] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    const res = await GetTrainTickets({ search, journeyDate, bookingStatus, paymentStatus });
    setLoading(false);
    if (res?.success) {
      setTickets(res.tickets || []);
    } else {
      setTickets([]);
      setError(res?.message || "Failed to load tickets");
    }
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const firstPassenger = (t) =>
    t.passengers && t.passengers.length ? t.passengers[0].passengerName : "—";

  const handleDuplicate = async (id) => {
    const res = await DuplicateTrainTicket(id);
    if (res?.success && res.ticket) {
      onEdit(mapTicketToForm(res.ticket));
    }
  };

  const handleArchive = async (id) => {
    if (!window.confirm("Archive this ticket?")) return;
    await ArchiveTrainTicket(id);
    load();
  };

  const handlePreview = async (id) => {
    const res = await PreviewTrainTicketById(id);
    if (res?.success) setPreviewHtml(res.previewHtml);
  };

  const handleEdit = async (id) => {
    const res = await GetTrainTicketById(id);
    if (res?.success) onEdit(mapTicketToForm(res.ticket));
  };

  return (
    <div className={classes.Box}>
      <div className={classes.ListHeader}>
        <h5>Train Tickets</h5>
        <Button variant="success" onClick={onCreateNew}>+ Create Train Ticket</Button>
      </div>

      {error && (
        <Alert variant="warning">
          {error}
          <div className={classes.Hint} style={{ marginTop: 6 }}>
            First-time setup: deploy the updated backend, run Flyway migrations V37 &amp; V38, then refresh.
          </div>
        </Alert>
      )}

      <Form onSubmit={handleSearch} className={classes.FilterForm}>
        <Row className={classes.FilterRow}>
          <Col xs={12} md={4} lg={3}>
            <Form.Control placeholder="Search booking, PNR, passenger..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </Col>
          <Col xs={6} md={3} lg={2}>
            <Form.Control type="date" value={journeyDate} onChange={(e) => setJourneyDate(e.target.value)} />
          </Col>
          <Col xs={6} md={3} lg={2}>
            <Form.Control as="select" value={bookingStatus} onChange={(e) => setBookingStatus(e.target.value)}>
              <option value="">All Booking Status</option>
              {BOOKING_STATUS_OPTIONS.map((o) => <option key={o}>{o}</option>)}
            </Form.Control>
          </Col>
          <Col xs={6} md={3} lg={2}>
            <Form.Control as="select" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
              <option value="">All Payment Status</option>
              {PAYMENT_STATUS_OPTIONS.map((o) => <option key={o}>{o}</option>)}
            </Form.Control>
          </Col>
          <Col xs={6} md={2} lg={2}>
            <Button type="submit" variant="primary" block>Filter</Button>
          </Col>
        </Row>
      </Form>

      <div className={classes.TableWrap}>
        <Table bordered hover size="sm" className={classes.TicketTable}>
          <thead>
            <tr>
              <th>Booking ID</th>
              <th>PNR</th>
              <th>Passenger</th>
              <th>Train</th>
              <th>From</th>
              <th>To</th>
              <th>Journey Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="text-center">Loading...</td></tr>
            )}
            {!loading && tickets.map((t) => (
              <tr key={t.id}>
                <td>{t.bookingId || "—"}</td>
                <td>{t.pnr || "—"}</td>
                <td>{firstPassenger(t)}</td>
                <td>{t.trainNumber || "—"}</td>
                <td>{t.boardingStationCode || t.boardingStation || "—"}</td>
                <td>{t.destinationStationCode || t.destinationStation || "—"}</td>
                <td>{t.journeyDate || "—"}</td>
                <td>{t.bookingStatus || "—"}</td>
                <td className={classes.ActionCell}>
                  <Button size="sm" variant="link" onClick={() => handleEdit(t.id)}>Edit</Button>
                  <Button size="sm" variant="link" onClick={() => handlePreview(t.id)}>Preview</Button>
                  <Button size="sm" variant="link" onClick={() => DownloadTrainTicketPdfById(t.id, t.pnr, t.bookingId)}>PDF</Button>
                  <Button size="sm" variant="link" onClick={() => handleDuplicate(t.id)}>Dup</Button>
                  <Button size="sm" variant="link" className="text-danger" onClick={() => handleArchive(t.id)}>Archive</Button>
                </td>
              </tr>
            ))}
            {!loading && !error && !tickets.length && (
              <tr>
                <td colSpan={9} className="text-center">
                  No tickets yet. Click <strong>+ Create Train Ticket</strong> to add one.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {previewHtml && (
        <div className={classes.InlinePreview}>
          <div className={classes.PreviewHeader}>
            <strong>Preview</strong>
            <Button size="sm" variant="secondary" onClick={() => setPreviewHtml("")}>Close</Button>
          </div>
          <div className={classes.PreviewFrame} dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </div>
      )}
    </div>
  );
};

export default TrainTicketList;
